import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderOpen,
  Calendar,
  FileText,
  MessageSquare,
  BookOpen,
  Users,
  Settings,
  BarChart3,
  Shield,
  Database,
  Activity,
  ChevronLeft,
  ChevronRight,
  Bell,
  FileText as FileTextIcon,
  Calendar as CalendarIcon,
  User,
  LineChart,
  ScrollText,
  Archive,
  Map
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useSidebarStore } from '../../store/sidebarStore';
import { ProfileType } from '../../types';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  roles: ProfileType[];
  badge?: number;
}

const navigation: NavigationItem[] = [
  {
    name: 'Tableau de bord',
    href: '/dashboard',
    icon: LayoutDashboard,
    roles: [ProfileType.PORTEUR, ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR, ProfileType.FINANCEUR, ProfileType.ADMIN]
  },
  {
    name: 'Vue d\'ensemble CRESS',
    href: '/coordinator/dashboard',
    icon: BarChart3,
    roles: [ProfileType.COORDINATEUR, ProfileType.ADMIN]
  },
  {
    name: 'Mes projets',
    href: '/projects',
    icon: FolderOpen,
    roles: [ProfileType.PORTEUR, ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR]
  },
  {
    name: 'Tous les projets',
    href: '/coordinator/projects',
    icon: FolderOpen,
    roles: [ProfileType.COORDINATEUR, ProfileType.ADMIN]
  },
  {
    name: 'Mes projets assignés',
    href: '/funder/projects',
    icon: FolderOpen,
    roles: [ProfileType.FINANCEUR]
  },
  {
    name: 'Projets Archivés',
    href: '/projects/archived',
    icon: Archive,
    roles: [ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR, ProfileType.ADMIN]
  },
  {
    name: 'Rendez-vous',
    href: '/rdv',
    icon: Calendar,
    roles: [ProfileType.PORTEUR, ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR, ProfileType.FINANCEUR]
  },
  {
    name: 'Feuille de route',
    href: '/roadmap',
    icon: Map,
    roles: [ProfileType.ACCOMPAGNATEUR]
  },
  {
    name: 'Calendrier Pro',
    href: '/advisor/calendar',
    icon: CalendarIcon,
    roles: [ProfileType.ACCOMPAGNATEUR]
  },
  {
    name: 'Templates Messages',
    href: '/advisor/templates',
    icon: MessageSquare,
    roles: [ProfileType.ACCOMPAGNATEUR]
  },
  {
    name: 'Rapports Activité',
    href: '/advisor/reports',
    icon: BarChart3,
    roles: [ProfileType.ACCOMPAGNATEUR]
  },
  {
    name: 'Alertes Projets',
    href: '/advisor/alerts',
    icon: Bell,
    roles: [ProfileType.ACCOMPAGNATEUR]
  },
  {
    name: 'Vérification documents',
    href: '/documents',
    icon: FileText,
    roles: [ProfileType.COORDINATEUR, ProfileType.ADMIN]
  },
  {
    name: 'Ressources',
    href: '/resources',
    icon: BookOpen,
    roles: [ProfileType.PORTEUR, ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR, ProfileType.FINANCEUR]
  },
  {
    name: 'Messages',
    href: '/messages',
    icon: MessageSquare,
    roles: [ProfileType.PORTEUR, ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR],
    badge: 2
  },
  {
    name: 'Configuration',
    href: '/setup',
    icon: Settings,
    roles: [ProfileType.ADMIN, ProfileType.COORDINATEUR]
  },
  {
    name: 'Vérification Supabase',
    href: '/setup',
    icon: Database,
    roles: [ProfileType.ADMIN, ProfileType.COORDINATEUR]
  },
  {
    name: 'Analyse Profils',
    href: '/analysis',
    icon: BarChart3,
    roles: [ProfileType.ADMIN, ProfileType.COORDINATEUR]
  },
];

const adminNavigation: NavigationItem[] = [
  {
    name: 'Monitoring Système',
    href: '/admin/dashboard',
    icon: LineChart,
    roles: [ProfileType.ADMIN]
  },
  {
    name: 'Gestion Utilisateurs',
    href: '/admin/users',
    icon: Users,
    roles: [ProfileType.ADMIN]
  },
  {
    name: 'Accompagnateurs',
    href: '/admin/accompagnateurs',
    icon: Users,
    roles: [ProfileType.ADMIN, ProfileType.COORDINATEUR]
  },
  {
    name: 'Porteurs de projet',
    href: '/admin/users/porteurs',
    icon: User,
    roles: [ProfileType.ADMIN, ProfileType.COORDINATEUR]
  },
  {
    name: 'Données ESS',
    href: '/admin/ess-data',
    icon: Database,
    roles: [ProfileType.ADMIN, ProfileType.COORDINATEUR]
  },
  {
    name: 'Contenus',
    href: '/admin/content',
    icon: ScrollText,
    roles: [ProfileType.ADMIN, ProfileType.COORDINATEUR]
  },
  {
    name: 'Audit & Logs',
    href: '/admin/audit',
    icon: ScrollText,
    roles: [ProfileType.ADMIN]
  },
  {
    name: 'Paramètres',
    href: '/admin/settings',
    icon: Settings,
    roles: [ProfileType.ADMIN]
  }
];

export const Sidebar: React.FC = () => {
  const { user } = useAuthStore();
  const { isOpen, isCollapsed, closeSidebar, toggleCollapsed } = useSidebarStore();
  const location = useLocation();

  const canAccessItem = (item: NavigationItem) => {
    return user?.profile_type && item.roles.includes(user.profile_type as ProfileType);
  };

  const renderNavItem = (item: NavigationItem) => {
    const isActive = location.pathname === item.href;
    
    return (
      <NavLink
        key={item.name}
        to={item.href}
        onClick={() => window.innerWidth < 1024 && closeSidebar()}
        className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'space-x-3 px-4'} py-3 rounded-xl transition-all duration-300 relative group ${
          isActive
            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 text-white shadow-lg transform scale-105'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:transform hover:scale-102 hover:shadow-md'
        }`}
        title={isCollapsed ? item.name : undefined}
      >
        <item.icon className={`w-5 h-5 transition-all duration-200 ${isActive ? 'animate-bounce-subtle' : ''}`} />
        {!isCollapsed && (
          <>
            <span className="font-medium transition-all duration-200">{item.name}</span>
            {item.badge && (
              <span className="absolute right-4 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                {item.badge}
              </span>
            )}
          </>
        )}
        {isCollapsed && item.badge && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center animate-pulse">
            {item.badge}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={closeSidebar}
        />
      )}
      
      <aside className={`
        ${isCollapsed ? 'w-20' : 'w-64'} 
        bg-white/95 dark:bg-gray-800/95 backdrop-blur-md border-r border-gray-200 dark:border-gray-700 
        min-h-screen transition-all duration-300 ease-in-out
        fixed lg:relative z-50 lg:z-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        animate-slide-up
      `}>
        {/* Collapse Toggle (Desktop) */}
        <div className="hidden lg:block absolute -right-3 top-20">
          <button
            onClick={toggleCollapsed}
            className="w-6 h-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-110 shadow-md"
          >
            {isCollapsed ? (
              <ChevronRight className="w-3 h-3 text-gray-600 dark:text-gray-400" />
            ) : (
              <ChevronLeft className="w-3 h-3 text-gray-600 dark:text-gray-400" />
            )}
          </button>
        </div>

        <nav className="p-4 h-full overflow-y-auto">
        {/* Main Navigation */}
        <div className="space-y-2 animate-slide-down">
          {navigation
            .filter(canAccessItem)
            .map(renderNavItem)
          }
        </div>

        {/* Admin Section */}
        {adminNavigation.some(canAccessItem) && (
          <div className="mt-8 animate-slide-down" style={{ animationDelay: '0.1s' }}>
            {!isCollapsed && (
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 mb-2">
              Administration
              </h3>
            )}
            <div className="space-y-2">
              {adminNavigation
                .filter(canAccessItem)
                .map(renderNavItem)
              }
            </div>
          </div>
        )}

        {/* Profile Badge */}
        <div className="mt-8 px-4 animate-slide-down" style={{ animationDelay: '0.2s' }}>
          <div className={`bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl ${isCollapsed ? 'p-2' : 'p-4'} border border-blue-200 dark:border-blue-800 transition-all duration-300 hover:shadow-lg hover:scale-105 group`}>
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-2'}`}>
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-200">
                <span className="text-white text-sm font-medium">
                  {user?.full_name?.charAt(0) || user?.email?.charAt(0)}
                </span>
              </div>
              {!isCollapsed && (
              <div className="transition-all duration-200">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {user?.full_name || user?.email?.split('@')[0]}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 truncate">
                  {user?.organization}
                </p>
              </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      </aside>
    </>
  );
};